import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Users, DollarSign, FileText, Wallet, AlertCircle, Loader } from 'lucide-react';
import Avatar from '../Avatar';
import { supabase, teamMembersService } from '../../services/supabaseService';

// Contabilidad para admin:
//   Por cada colaborador, muestra en el rango de fechas seleccionado:
//   - Horas aprobadas (importe de timesheets con status='approved')
//   - Facturado (suma de user_invoices con status validated|paid)
//   - Pagado (suma de payments con status='executed')
//   - Debido = Facturado - Pagado (o Horas aprobadas - Pagado si no ha facturado)
//
// El rango por defecto es el mes en curso.

type Row = {
  userId: string;
  name: string;
  email: string;
  avatar?: string;
  approvedAmount: number;
  invoicedAmount: number;
  paidAmount: number;
  owed: number;
};

const AccountingTab: React.FC = () => {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const [from, setFrom] = useState<string>(monthStart.toISOString().split('T')[0]);
  const [to, setTo] = useState<string>(monthEnd.toISOString().split('T')[0]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [collaboratorFilter, setCollaboratorFilter] = useState<string>('');

  useEffect(() => {
    load();
  }, [from, to]);

  const load = async () => {
    setLoading(true);
    try {
      const team = await teamMembersService.getAll();
      const activeIds = team.filter((t: any) => t.isActive).map((t: any) => t.id);

      const [tsRes, invRes, payRes] = await Promise.all([
        supabase
          .from('timesheets')
          .select('user_id, calculated_amount')
          .eq('status', 'approved')
          .gte('date', from)
          .lte('date', to),
        supabase
          .from('user_invoices')
          .select('user_id, amount, status')
          .in('status', ['validated', 'paid'])
          .gte('date', from)
          .lte('date', to),
        supabase
          .from('payments')
          .select('user_id, amount, status')
          .eq('status', 'executed')
          .gte('payment_date', from)
          .lte('payment_date', to),
      ]);

      const map: Record<string, Row> = {};
      team.forEach((m: any) => {
        map[m.id] = {
          userId: m.id,
          name: m.name || 'Colaborador',
          email: m.email,
          avatar: m.avatar,
          approvedAmount: 0,
          invoicedAmount: 0,
          paidAmount: 0,
          owed: 0,
        };
      });

      (tsRes.data || []).forEach((t: any) => {
        if (map[t.user_id]) map[t.user_id].approvedAmount += Number(t.calculated_amount || 0);
      });
      (invRes.data || []).forEach((i: any) => {
        if (map[i.user_id]) map[i.user_id].invoicedAmount += Number(i.amount || 0);
      });
      (payRes.data || []).forEach((p: any) => {
        if (map[p.user_id]) map[p.user_id].paidAmount += Number(p.amount || 0);
      });

      const result = Object.values(map).map(r => ({
        ...r,
        // Deuda: preferimos usar lo facturado (fuente de verdad de lo que
        // le tenemos que pagar). Si no ha facturado, cae a lo aprobado.
        owed: (r.invoicedAmount > 0 ? r.invoicedAmount : r.approvedAmount) - r.paidAmount,
      }));

      // Solo colaboradores activos + con actividad en el periodo o con deuda.
      const filtered = result.filter(r =>
        activeIds.includes(r.userId) &&
        (r.approvedAmount > 0 || r.invoicedAmount > 0 || r.paidAmount > 0 || r.owed !== 0)
      );

      setRows(filtered);
    } catch (err) {
      console.error('Error cargando contabilidad:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!collaboratorFilter.trim()) return rows;
    const q = collaboratorFilter.toLowerCase();
    return rows.filter(r =>
      r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)
    );
  }, [rows, collaboratorFilter]);

  const totals = useMemo(
    () => filteredRows.reduce(
      (acc, r) => ({
        approved: acc.approved + r.approvedAmount,
        invoiced: acc.invoiced + r.invoicedAmount,
        paid: acc.paid + r.paidAmount,
        owed: acc.owed + r.owed,
      }),
      { approved: 0, invoiced: 0, paid: 0, owed: 0 }
    ),
    [filteredRows]
  );

  const setPreset = (preset: 'month' | 'prev_month' | 'ytd' | 'last_30') => {
    const today = new Date();
    let f = new Date();
    let t = today;
    if (preset === 'month') {
      f = new Date(today.getFullYear(), today.getMonth(), 1);
      t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (preset === 'prev_month') {
      f = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      t = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === 'ytd') {
      f = new Date(today.getFullYear(), 0, 1);
      t = today;
    } else if (preset === 'last_30') {
      f = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      t = today;
    }
    setFrom(f.toISOString().split('T')[0]);
    setTo(t.toISOString().split('T')[0]);
  };

  const fmt = (n: number) => `${n.toLocaleString('es-ES', { maximumFractionDigits: 2 })} €`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Contabilidad</h2>
        <p className="text-sm text-gray-600">
          Aprobado, facturado, pagado y saldo pendiente por colaborador.
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Desde</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Hasta</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setPreset('month')} className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            Este mes
          </button>
          <button onClick={() => setPreset('prev_month')} className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            Mes pasado
          </button>
          <button onClick={() => setPreset('last_30')} className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            Últimos 30 días
          </button>
          <button onClick={() => setPreset('ytd')} className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            Año en curso
          </button>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Buscar</label>
          <input
            type="search"
            value={collaboratorFilter}
            onChange={(e) => setCollaboratorFilter(e.target.value)}
            placeholder="Nombre o email…"
            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Aprobado" value={fmt(totals.approved)} icon={Calendar} tone="blue" />
        <KpiCard label="Facturado" value={fmt(totals.invoiced)} icon={FileText} tone="amber" />
        <KpiCard label="Pagado" value={fmt(totals.paid)} icon={Wallet} tone="emerald" />
        <KpiCard label="Pendiente" value={fmt(totals.owed)} icon={AlertCircle}
                 tone={totals.owed > 0.005 ? 'red' : 'emerald'}
                 highlight={totals.owed > 0.005} />
      </div>

      {/* Tabla por colaborador */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader className="w-5 h-5 animate-spin mr-2" /> Cargando…
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
            <Users className="w-8 h-8 text-gray-300" />
            Sin movimientos en este rango.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Colaborador</th>
                  <th className="px-4 py-2 text-right">Aprobado</th>
                  <th className="px-4 py-2 text-right">Facturado</th>
                  <th className="px-4 py-2 text-right">Pagado</th>
                  <th className="px-4 py-2 text-right">Pendiente</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredRows.map(r => (
                  <tr key={r.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar name={r.name} src={r.avatar} size="sm" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{r.name}</p>
                          <p className="text-xs text-gray-500 truncate">{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">{fmt(r.approvedAmount)}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{fmt(r.invoicedAmount)}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{fmt(r.paidAmount)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${
                      r.owed > 0.005 ? 'text-red-700' : r.owed < -0.005 ? 'text-emerald-700' : 'text-gray-700'
                    }`}>
                      {fmt(r.owed)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 text-sm font-semibold">
                <tr>
                  <td className="px-4 py-2 text-gray-700">Total</td>
                  <td className="px-4 py-2 text-right">{fmt(totals.approved)}</td>
                  <td className="px-4 py-2 text-right">{fmt(totals.invoiced)}</td>
                  <td className="px-4 py-2 text-right">{fmt(totals.paid)}</td>
                  <td className={`px-4 py-2 text-right ${
                    totals.owed > 0.005 ? 'text-red-700' : 'text-gray-700'
                  }`}>
                    {fmt(totals.owed)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  icon: React.ElementType;
  tone: 'blue' | 'amber' | 'emerald' | 'red';
  highlight?: boolean;
}> = ({ label, value, icon: Icon, tone, highlight }) => {
  const tones = {
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    iconBg: 'bg-blue-100'    },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   iconBg: 'bg-amber-100'   },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', iconBg: 'bg-emerald-100' },
    red:     { bg: 'bg-red-50',     text: 'text-red-700',     iconBg: 'bg-red-100'     },
  }[tone];
  return (
    <div className={`rounded-lg p-4 border ${highlight ? 'border-red-300 ' + tones.bg : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
        <div className={`p-1.5 rounded ${tones.iconBg}`}>
          <Icon className={`w-4 h-4 ${tones.text}`} />
        </div>
      </div>
      <p className={`text-xl font-bold ${highlight ? tones.text : 'text-gray-900'}`}>{value}</p>
    </div>
  );
};

export default AccountingTab;
